import { AgentChatInfo, ChatMessage } from "../types";
import { apiCall } from "./firebase";

export class AgentMessagingService {
  // Cache for agent chat info
  private static agentCache: { data: AgentChatInfo[] | null; timestamp: number } = {
    data: null,
    timestamp: 0,
  };

  // Cache duration: 5 minutes
  private static CACHE_DURATION = 5 * 60 * 1000;

  /**
   * Check if cache is valid
   */
  private static isCacheValid(): boolean {
    return this.agentCache.data !== null && Date.now() - this.agentCache.timestamp < this.CACHE_DURATION;
  }

  /**
   * Invalidate the agent cache
   */
  static invalidateAgentCache(): void {
    this.agentCache.data = null;
    this.agentCache.timestamp = 0;
    console.log("AgentMessagingService: Agent cache invalidated");
  }

  /**
   * Get cached agent data or fetch fresh data
   */
  private static async getCachedAgentData(): Promise<AgentChatInfo[]> {
    if (this.isCacheValid()) {
      console.log("AgentMessagingService: Using cached agent data");
      return this.agentCache.data!;
    }

    console.log("AgentMessagingService: Fetching fresh agent data");
    const response = await apiCall("/getAgentChatInfo", {
      method: "GET",
    });

    console.log("AgentMessagingService: API response:", response);

    // Convert Firebase timestamps to Date objects
    const agentChatInfos = response.agentChatInfos.map((agent: any) => ({
      ...agent,
      lastMessageTime: agent.lastMessageTime ? this.convertFirebaseTimestamp(agent.lastMessageTime) : undefined,
    }));

    console.log(
      "AgentMessagingService: Available agent IDs:",
      agentChatInfos.map((a: AgentChatInfo) => ({ agentId: a.agentId, agentName: a.agentName }))
    );
    console.log("AgentMessagingService: Returning agentChatInfos:", agentChatInfos);

    // Update cache
    this.agentCache.data = agentChatInfos;
    this.agentCache.timestamp = Date.now();

    return agentChatInfos;
  }

  /**
   * Send a user message to an agent
   */
  static async sendUserMessageToAgent(agentId: string, content: string): Promise<void> {
    try {
      // Debug logging
      console.log("Frontend sendUserMessageToAgent: agentId:", agentId);
      console.log("Frontend sendUserMessageToAgent: content:", content);

      const requestBody = {
        agentId,
        content,
      };
      console.log("Frontend sendUserMessageToAgent: Request body:", requestBody);

      await apiCall("/sendUserMessageToAgent", {
        method: "POST",
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      console.error("Error sending user message to agent:", error);
      throw error;
    }
  }

  /**
   * Convert Firebase timestamp to Date
   */
  private static convertFirebaseTimestamp(timestamp: any): Date {
    if (timestamp && typeof timestamp === "object" && "_seconds" in timestamp) {
      return new Date(timestamp._seconds * 1000);
    }
    if (timestamp instanceof Date) {
      return timestamp;
    }
    if (typeof timestamp === "string" || typeof timestamp === "number") {
      return new Date(timestamp);
    }
    console.warn("Invalid timestamp format:", timestamp);
    return new Date();
  }

  /**
   * Get chat messages for an agent (includes user messages, agent responses, events, sources, actions)
   */
  static async getChatMessages(agentId: string, limit = 25, beforeTimestamp?: number): Promise<{ messages: ChatMessage[]; pagination: any }> {
    try {
      let url = `/getThreadMessages?agentId=${agentId}&limit=${limit}`;
      if (beforeTimestamp) {
        url += `&beforeTimestamp=${beforeTimestamp}`;
      }

      const response = await apiCall(url);

      // Convert Firebase timestamps to Date objects
      const messages = response.messages.map((message: any) => ({
        ...message,
        timestamp: this.convertFirebaseTimestamp(message.timestamp),
      }));

      return {
        messages,
        pagination: response.pagination,
      };
    } catch (error) {
      console.error("Error getting chat messages:", error);
      throw error;
    }
  }

  /**
   * Get chat messages only (for main chat interface)
   */
  static async getChatMessagesOnly(agentId: string, limit = 50): Promise<ChatMessage[]> {
    try {
      const response = await apiCall(`/getThreadMessages?agentId=${agentId}&limit=${limit}`);

      if (!response.success) {
        throw new Error(response.error || "Failed to get chat messages");
      }

      const messages = response.messages.map((msg: any) => ({
        ...msg,
        timestamp: new Date(msg.timestamp),
        timestampMs: msg.timestampMs || new Date(msg.timestamp).getTime(),
      }));

      return messages;
    } catch (error) {
      console.error("Error getting chat messages only:", error);
      throw error;
    }
  }

  /**
   * Get agent chat info for the current user
   */
  static async getAgentChatInfo(): Promise<AgentChatInfo[]> {
    console.log("AgentMessagingService: getAgentChatInfo - Starting");
    try {
      return await this.getCachedAgentData();
    } catch (error) {
      console.error("AgentMessagingService: Error getting agent chat info:", error);
      console.error("AgentMessagingService: Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : undefined,
      });
      throw error;
    }
  }

  /**
   * Mark messages as read for a thread
   */
  static async markMessagesAsRead(threadId: string): Promise<void> {
    try {
      await apiCall("/markMessagesAsRead", {
        method: "POST",
        body: JSON.stringify({
          threadId,
        }),
      });
    } catch (error) {
      console.error("Error marking messages as read:", error);
      throw error;
    }
  }

  /**
   * Refresh agent subscriptions
   */
  static async refreshAgentSubscriptions(agentId: string): Promise<void> {
    try {
      await apiCall("/refreshAgentSubscriptions", {
        method: "POST",
        body: JSON.stringify({
          agentId,
        }),
      });
    } catch (error) {
      console.error("Error refreshing agent subscriptions:", error);
      throw error;
    }
  }

  /**
   * Refresh all agent subscriptions for the current user
   */
  static async refreshAllAgentSubscriptions(): Promise<void> {
    try {
      await apiCall("/refreshAllAgentSubscriptions", {
        method: "POST",
      });
    } catch (error) {
      console.error("Error refreshing all agent subscriptions:", error);
      throw error;
    }
  }

  /**
   * Get subscriptions for the current user (debug method)
   */
  static async getSubscriptions(): Promise<any[]> {
    try {
      const response = await apiCall("/getSubscriptionsForUser");
      return response.subscriptions || [];
    } catch (error) {
      console.error("Error getting subscriptions:", error);
      throw error;
    }
  }

  /**
   * Get all thread messages for debugging
   */
  static async getAllThreadMessages(): Promise<any> {
    try {
      const response = await apiCall("/getAllThreadMessages");
      return response;
    } catch (error) {
      console.error("Error getting all thread messages:", error);
      throw error;
    }
  }

  /**
   * Execute an action by ID
   */
  static async executeAction(actionId: string, overridePermissions = false): Promise<any> {
    try {
      console.log("AgentMessagingService: Executing action by ID:", actionId, "override:", overridePermissions);

      // Execute the action by ID
      const response = await apiCall("/executeActionById", {
        method: "POST",
        body: JSON.stringify({
          actionId,
          overridePermissions,
        }),
        headers: overridePermissions
          ? {
              "x-frontend-override": "true",
            }
          : undefined,
      });

      console.log("AgentMessagingService: Action execution response:", response);
      return response.result;
    } catch (error) {
      console.error("AgentMessagingService: Error executing action by ID:", error);
      throw error;
    }
  }

  /**
   * Ignore an action by ID
   */
  static async ignoreAction(actionId: string): Promise<any> {
    try {
      console.log("AgentMessagingService: Ignoring action by ID:", actionId);

      const response = await apiCall("/ignoreAction", {
        method: "POST",
        body: JSON.stringify({
          actionId,
        }),
      });

      console.log("AgentMessagingService: Action ignore response:", response);
      return response;
    } catch (error) {
      console.error("AgentMessagingService: Error ignoring action by ID:", error);
      throw error;
    }
  }
}
